import groupBy from 'lodash/groupBy'
import isNil from 'lodash/isNil'
import mapValues from 'lodash/mapValues'

import { Potential_Property_Bool_Exp } from '@/types/cate-graphql-generated-types'
import { Filter } from '@/types/graphql-generated-types'
import { SortBy, SortOptions } from '@/types/sort'
import { mapIntRangeFilter, mapSortDirection } from '@/utils/graphql-filter-mapper'

type Zones = {
  label: string
  slug: string
}[]

export const getZoneFromSegment = (zones: Zones, segment: string) => {
  return segment ? zones.find((item) => item.slug && item?.slug.split('/')[2] === segment) : null
}

const mapResidentialType = (residentialTypeFilter: string) => {
  const residentialTypeEnumMap = {
    APARTMENT: 'apartment',
    ATTIC: 'attic',
    HOUSE: 'house',
    LOFT: 'loft',
    MANSARD: 'mansard',
    TOWNHOUSE: 'townhouse',
    VILLA: 'villa',
  }
  return residentialTypeEnumMap?.[residentialTypeFilter] || residentialTypeEnumMap
}

const mapHeatingSystem = (lisaHeatingSystemFilter: string) => {
  const heatingEnumMap = {
    AUTONOMOUS: 'independent',
    SHARED: 'central',
  }
  return heatingEnumMap?.[lisaHeatingSystemFilter] || lisaHeatingSystemFilter
}

export const mapStateOfMaintenance = (lisaStateOfMaintenanceFilterList: string[]) => {
  const maintenanceEnumMap = {
    FULLY_RENOVATED: 'fully_renovated',
    HABITABLE: 'habitable',
    NEED_RENOVATION: 'need_renovation',
    NEW: 'new',
    RENOVATED: 'renovated',
    UNDER_CONSTRUCTION: 'under_construction',
  }
  return lisaStateOfMaintenanceFilterList.reduce((acc, item) => {
    if (item === 'RENOVATED') return [...acc, maintenanceEnumMap.RENOVATED, maintenanceEnumMap.FULLY_RENOVATED]
    const mappedValue = maintenanceEnumMap?.[item] || item
    return [...acc, mappedValue]
  }, [])
}

const mapStateAtDeed = (lisaStateAtDeedFilter: string) => {
  const deedEnumMap = {
    FREE: 'free',
    OCCUPIED: 'occupied',
  }
  return deedEnumMap?.[lisaStateAtDeedFilter] || lisaStateAtDeedFilter
}

export const getPriorityWhereCondition = (filter: Filter): Potential_Property_Bool_Exp => {
  const { 2: cities, 3: zones } = mapValues(
    groupBy(
      filter.byGeoSlug?.map((zone) => zone.split('/')),
      'length'
    ),
    (value) => value.map((item) => item.join('/'))
  )

  return {
    _and: [
      { isPresentable: { _eq: true } },
      { title: { _is_null: false } },
      { rooms: { _is_null: false } },
      { slug: { _is_null: false } },
      { commercialArea: { _is_null: false } },
      { bathrooms: { _is_null: false } },
      { entranceFloor: { _is_null: false } },
      { stateOfMaintenance: { _is_null: false } },
      { residentialType: { _is_null: false } },
      { insertedAt: { _is_null: false } },
      { askedPriceInCents: { _is_null: false } },
      { _or: [...(zones ? [{ zoneSlug: { _in: zones } }] : []), ...(cities ? [{ city: { _in: cities } }] : [])] },

      ...(filter?.byBathrooms ? [{ bathrooms: mapIntRangeFilter(filter.byBathrooms) }] : []),

      ...(filter?.byPrice
        ? [
            {
              _or: [
                { askedPriceInCents: mapIntRangeFilter(mapValues(filter.byPrice, (x) => (isNil(x) ? null : x * 100))) },
                {
                  valuationPriceInCents: mapIntRangeFilter(
                    mapValues(filter.byPrice, (x) => (isNil(x) ? null : x * 100))
                  ),
                },
              ],
            },
          ]
        : []),
      ...(filter?.byRooms ? [{ rooms: mapIntRangeFilter(filter.byRooms) }] : []),
      ...(filter?.byArea ? [{ commercialArea: mapIntRangeFilter(filter.byArea) }] : []),
      // tipologia
      ...(filter?.byResidentialType
        ? [{ residentialType: { _in: filter.byResidentialType.map(mapResidentialType) } }]
        : []),
      // no bedrooms
      ...(filter?.byBathrooms ? [{ bathrooms: mapIntRangeFilter(filter.byBathrooms) }] : []),

      // TBD piano - byFloors

      // stateOfMaitenance
      ...(filter.byStateOfMaintenance
        ? [{ stateOfMaintenance: { _in: mapStateOfMaintenance(filter.byStateOfMaintenance) } }]
        : []),

      // stateAtDeed
      ...(filter.byStateAtDeed ? [{ stateAtDeed: { _in: filter.byStateAtDeed.map(mapStateAtDeed) } }] : []),

      ...(filter?.onlyWithElevator ? [{ hasElevator: { _eq: true } }] : []),
      // no parking
      ...(filter?.onlyWithBox ? [{ boxAreas: { _is_null: false } }] : []),
      // no basement cantina
      ...(filter?.onlyWithGarden ? [{ gardenArea: { _is_null: false } }] : []),
      ...(filter?.onlyWithBalcony ? [{ balconies: { _is_null: false } }] : []),
      ...(filter?.onlyWithTerrace ? [{ terraceAreas: { _is_null: false } }] : []),
      ...(filter?.onlyWithPool ? [{ hasPool: { _eq: true } }] : []),
      // no aria condizionata
      // no concierge
      ...(filter?.byHeatingSystem ? [{ heatingSystem: { _in: filter.byHeatingSystem.map(mapHeatingSystem) } }] : []),
      ...(filter?.byConstructionYear ? [{ constructionYear: mapIntRangeFilter(filter.byConstructionYear) }] : []),
    ],
  }
}

export const getPriorityGroupBy = ({ by, direction }: SortOptions) => {
  if (!by) {
    return {
      updatedAt: mapSortDirection(direction),
    }
  }
  switch (by) {
    case SortBy.Area:
      return {
        commercialArea: mapSortDirection(direction),
      }
    case SortBy.Date:
    case SortBy.Ranking:
      return {
        updatedAt: mapSortDirection(direction),
      }
    case SortBy.Price:
      return {
        askedPriceInCents: mapSortDirection(direction),
      }
    default:
      return {
        updatedAt: mapSortDirection(direction),
      }
  }
}
